import {Component, computed, inject, OnDestroy, OnInit, signal} from '@angular/core';
import {Panel} from 'primeng/panel';
import {TableModule} from 'primeng/table';
import {CommonModule} from '@angular/common';
import {FormsModule} from '@angular/forms';
import {Button} from 'primeng/button';
import {IconField} from 'primeng/iconfield';
import {InputIcon} from 'primeng/inputicon';
import {InputText} from 'primeng/inputtext';
import {Divider} from 'primeng/divider';
import {ConfirmDialogModule} from 'primeng/confirmdialog';
import {ConfirmationService} from 'primeng/api';
import {MessageService} from 'primeng/api';
import {DialogModule} from 'primeng/dialog';
import {ToastModule} from 'primeng/toast';
import { CommonEffects } from '../store/common.effects';
import { Store } from '@ngrx/store';
import { Actions, ofType } from '@ngrx/effects';
import { selectZoneData, selectZoneTotal } from '../store/common.selector'
import { Zone } from '../../shared/models/common/zone.model';
import { selectIsLoadingAction } from '../../shared/store/global';
import { Observable, Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged, takeUntil } from 'rxjs/operators';
import * as action from '../store/common.actions';
import {toSignal} from '@angular/core/rxjs-interop';
import {BaseListComponent} from '../../shared/components/base-list.component';
import { AssetHistoryDialogComponent } from '../../shared/components/asset-history-dialog/asset-history-dialog.component';
import { ColumnFilters } from '../../shared/models/ui/table-column.model';

@Component({
  selector: 'zones-list',
  templateUrl: './zones-list.component.html',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    Panel,
    TableModule,
    Button,
    IconField,
    InputIcon,
    InputText,
    Divider,
    ConfirmDialogModule,
    DialogModule,
    ToastModule,
    AssetHistoryDialogComponent
  ],
  providers: [ConfirmationService, MessageService, CommonEffects],
  styleUrls: ['./zones-list.component.scss']
})
export class ZonesListComponent extends BaseListComponent implements OnInit, OnDestroy {

  private messageService = inject(MessageService);
  private store$ = inject(Store<{ common: any }>)
  private actions$ = inject(Actions);

  zones = toSignal(this.store$.select(selectZoneData), {initialValue: []});
  totalRecords = toSignal(this.store$.select(selectZoneTotal), {initialValue: 0});
  loading = toSignal(this.store$.select(selectIsLoadingAction('[Common] Find Zones')), {initialValue: false});

  
  readonly auditResourceName = 'Zone';
  historyVisible = false;
  historyRecordId: number | string | null = null;
  historyRecordLabel = '';

  selectedZones = signal<Zone[]>([]);

  // Add Zone Form Variables
  dialogVisible = signal(false);
  zoneName = signal('');
  zoneCode = signal('');
  savingZone = signal(false);
  isEditMode = signal(false);
  editingZoneId = signal<number | null>(null);

  deleteButtonLabel = computed(() => {
    return this.selectedZones().length > 1 ? 'Delete Multiple' : 'Delete';
  });

  override ngOnInit(): void {
    super.ngOnInit();

    // Success Alert
    this.actions$.pipe(
      ofType(action.createZoneSuccess, action.updateZoneSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingZone.set(false);
      this.closeDialog();
      this.loadData(Math.floor(this.first / this.rows) + 1, this.rows, this.columnFilters);
    })

    // Error Alert
    this.actions$.pipe(
      ofType(action.deleteZoneSuccess),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.selectedZones.set([]);
      this.loadZones(); 
    })

    // Handle Error
    this.actions$.pipe(
      ofType(action.createZoneError, action.updateZoneError, action.deleteZoneError),
      takeUntil(this.destroy$)
    ).subscribe(() => {
      this.savingZone.set(false);
    })
  }

  override ngOnDestroy(): void {
    super.ngOnDestroy();
 }

  override loadData(page: number, size: number, filters: ColumnFilters): void {
    const keywords = filters['keywords'] || '';
    this.store$.dispatch(action.findZones({
      page,
      size,
      keywords
    }));
  }

  loadZones(): void {
    this.store$.dispatch(action.findZones({ page: 1, size: this.rows, keywords: ''}))
  }

  addNew(): void {
    this.zoneName.set('');
    this.zoneCode.set('');
    this.isEditMode.set(false);
    this.dialogVisible.set(true);
    this.editingZoneId.set(null);
  }

  closeDialog(): void {
    this.zoneName.set('');
    this.zoneCode.set('');
    this.savingZone.set(false);
    this.isEditMode.set(false);
    this.dialogVisible.set(false);
    this.editingZoneId.set(null);
  }

  saveZone(): void {
    if(!this.zoneName().trim() || !this.zoneCode().trim()) {
      this.messageService.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Zone name and zone code are required'
      });
      return;
    }

    this.savingZone.set(true);

    const zoneData: Zone = {
      id: this.editingZoneId() || 0,
      zone_name: this.zoneName().trim(),
      zone_code: this.zoneCode().trim()
    };

    if(this.isEditMode() && this.editingZoneId()) {
      this.store$.dispatch(action.updateZone({ id: this.editingZoneId()!, zone: zoneData}))
    } else {
      this.store$.dispatch(action.createZone({ zone: zoneData }))
    }
  }

  editZone(): void {
    if (this.selectedZones().length === 1) {
      const ZoneToEdit = this.selectedZones()[0];
      this.zoneName.set(ZoneToEdit.zone_name ? ZoneToEdit.zone_name : '');
      this.zoneCode.set(ZoneToEdit.zone_code ? ZoneToEdit.zone_code : '');
      this.isEditMode.set(true);
      this.editingZoneId.set(ZoneToEdit.id);
      this.dialogVisible.set(true);
    } else {
      this.messageService.add({
        severity: 'warn',
        summary: 'Warning',
        detail: 'Please select exactly one zone to edit'
      });
    }
  }

  deleteZone(): void {
    if (this.selectedZones().length === 0) {
      return;
    }

    const zonesToDelete = [...this.selectedZones()];
    const count = zonesToDelete.length;
    
    let message: string;
    if (count === 1) {
      message = `Are you sure you want to delete "${zonesToDelete[0].zone_name}"?`;
    } else {
      const maxDisplay = 10;
      const codesToShow = zonesToDelete.slice(0, maxDisplay);
      const codesList = codesToShow.map((z, index) => `${index + 1}. ${z.zone_code}`).join('<br>');
      
      let fullMessage = `Are you sure you want to delete ${count} zones?<br><br><strong>Codes to be deleted:</strong><br>${codesList}`;
      
      if (count > maxDisplay) {
        fullMessage += '<br>etc.';
      }
      
      message = fullMessage;
    }

    this.confirmationService.confirm({
      message: message,
      header: 'Confirm Delete',
      icon: 'pi pi-exclamation-triangle',
      acceptButtonStyleClass: 'p-button-danger',
      accept: () => {
        zonesToDelete.forEach(zone => {
          this.store$.dispatch(action.deleteZone({ id: zone.id }));
        });
      }
    });
  }

  refreshZones(): void {
    this.selectedZones.set([]);
    this.clearFilter();
    this.messageService.add({
      severity: 'info',
      summary: 'Refreshed',
      detail: 'Zones list has been refreshed'
    });
  }

  showHistory(): void {
    const selected = this.selectedZones();
    if (selected.length !== 1) {
      this.messageService.add({
        severity: 'info',
        summary: 'History',
        detail: 'Select one record to view history'
      });
      return;
    }
    const item = selected[0] as any;
    this.historyRecordId = item?.id ?? null;
    this.historyRecordLabel = item?.zone_name || item?.zone_code || item?.name || String(this.historyRecordId ?? '');
    this.historyVisible = true;
  }
}

